import { Button, UserAvatar } from "@somethings-ui/web";
import { formatRecordJoinedLabel } from "./format-record-joined-label";
import { useNavigate } from "@tanstack/react-router";
import { useGetUserMentorships } from "@/lib/api/mentorships";
import {
  useCreateInternalOpsChannel,
  useFindInternalOpsChannelByRecipients,
} from "@/lib/api/internal-ops-messaging";
import { useUserRoles } from "@/lib/api/roles";
import type { User } from "@/lib/api/user";
import { useToast } from "@/components/ui/use-toast";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events";
import { useDevOverridesStore } from "@/lib/hooks/admin/use-dev-overrides";
import {
  buildRedactedPseudoId,
  getRecordDisplayName,
} from "@/lib/redacted-display";
import { recordRoleLabels } from "@/lib/utils/role-label";
import { isMenteeIdentity } from "@/lib/utils/system-roles";
import { recordAccountStatus } from "./record-account-status";
import {
  recordUsesSupportChat,
  resolveMessageChannel,
} from "./resolve-message-channel";
import { RecordStatusBadge } from "./record-status-badge";
import type { UserRecordSurface } from "./user-record-tabs";

interface UserRecordHeaderProps {
  user: User;
  surface: UserRecordSurface;
}

export function UserRecordHeader({ user, surface }: UserRecordHeaderProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const opensSupportChat = recordUsesSupportChat(user);
  const { data: mentorships } = useGetUserMentorships(
    user.id,
    surface === "record" && !opensSupportChat,
  );
  const messageHref = resolveMessageChannel(mentorships);
  const { data: supportChannel } = useFindInternalOpsChannelByRecipients(
    opensSupportChat ? [user.id] : [],
  );
  const createSupportChannel = useCreateInternalOpsChannel();
  const { track } = useTrackEvent();
  const hideMenteeIdentity =
    useDevOverridesStore((state) => state.hidePhiPii) === true &&
    isMenteeIdentity(user.systemRoles);
  const displayName = hideMenteeIdentity
    ? buildRedactedPseudoId(user.id)
    : getRecordDisplayName(user);
  const photo = useSignedMedia(
    hideMenteeIdentity ? null : user.profile_picture_url,
  );
  const joinedLabel = formatRecordJoinedLabel(user.created_at);
  const { data: assignedRoles } = useUserRoles(user.id);
  const roleLabels = recordRoleLabels(
    assignedRoles?.map((assignment) => assignment.role.name) ??
      user.systemRoles,
  );
  const isRecord = surface === "record";
  const accountStatus = recordAccountStatus(user);

  const subline = [user.pronouns, joinedLabel ? `Joined ${joinedLabel}` : null]
    .filter(Boolean)
    .join(" · ");

  const openChat = async () => {
    track(PostHogEvents.USER_RECORD_MESSAGE_CLICKED, {
      user_name: displayName,
    });

    if (opensSupportChat) {
      try {
        const channel =
          supportChannel ??
          (await createSupportChannel.mutateAsync({
            recipientIds: [user.id],
          }));
        await navigate({
          to: "/conversations",
          search: {
            segment: "support",
            channel: channel.sdk_url,
            internalChannelId: channel.id,
          },
        });
      } catch {
        toast({
          title: "Couldn't open chat",
          description: "Please try again.",
          variant: "destructive",
        });
      }
      return;
    }

    if (!messageHref) return;
    await navigate({
      to: "/mentorships/$mentorshipId",
      params: { mentorshipId: messageHref },
    });
  };

  return (
    <header className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-surfaces-z4 bg-surfaces-z0 px-4 py-3">
      <div className="flex min-w-0 items-center gap-3">
        <div className="relative shrink-0">
          <UserAvatar
            name={hideMenteeIdentity ? "?" : displayName}
            imageUrl={hideMenteeIdentity ? undefined : photo.url}
            size={40}
          />
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="truncate font-heading text-xl font-bold leading-6 text-text-primary">
              {displayName}
            </h1>
            {roleLabels.map((roleLabel) => (
              <span
                key={roleLabel}
                className="inline-flex h-6 items-center rounded-full bg-[#f6f4ef] px-2.5 font-body text-xs font-medium text-[#65593e]"
              >
                {roleLabel}
              </span>
            ))}
            {isRecord || accountStatus === "in_training" ? (
              <RecordStatusBadge status={accountStatus} />
            ) : null}
          </div>
          {subline ? (
            <p className="mt-0.5 font-body text-sm leading-5 text-text-secondary">
              {subline}
            </p>
          ) : null}
        </div>
      </div>

      {isRecord ? (
        <Button
          size="sm"
          fullWidth={false}
          disabled={!opensSupportChat && !messageHref}
          loading={createSupportChannel.isPending}
          onPress={() => {
            void openChat();
          }}
        >
          Show Chat
        </Button>
      ) : null}
    </header>
  );
}
